import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import Icons from '../components/Icons';
import Avatar from '../components/ui/Avatar';
import StudentPortalBanner from '../components/student/StudentPortalBanner';
import { useStudentPortal } from '../store/StudentPortalContext';
import { useSettings } from '../store/SettingsContext';
import { useDisplaySettings } from '../store/DisplaySettingsContext';
import { useFeaturePacks } from '../store/FeaturePackContext';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: Icons.Dashboard, path: '/student-portal' },
  { id: 'timetable', label: 'Timetable', icon: Icons.Calendar, path: '/student-portal/timetable' },
  { id: 'assignments', label: 'Assignments', icon: Icons.Document, path: '/student-portal/assignments' },
  { id: 'journal', label: 'Journal', icon: Icons.Clipboard, path: '/student-portal/journal' },
  { id: 'rewards', label: 'Rewards', icon: Icons.Star, path: '/student-portal/rewards' },
  { id: 'notifications', label: 'Notifications', icon: Icons.Bell, path: '/student-portal/notifications' },
  { id: 'support', label: 'Support & Wellbeing', icon: Icons.Shield, path: '/student-portal/support' },
  { id: 'emergency', label: 'Emergency Contacts', icon: Icons.Alert, path: '/student-portal/emergency' }
];

export default function StudentPortalShell() {
  const { student, signOut } = useStudentPortal();
  const { settings } = useSettings();
  const { settings: displaySettings, setSettings: setDisplaySettings } = useDisplaySettings();
  const { packs } = useFeaturePacks();
  const portalSettings = settings?.studentPortal || {};
  const navigate = useNavigate();
  const visibleItems = navItems.filter((item) => {
    if (item.id === 'rewards' && packs?.rewards_points === false) return false;
    return true;
  });

  return (
    <div className="sp-shell sp-shell--portal">
      <a href="#student-portal-main" className="sp-skip-link">Skip to content</a>
      <aside className="sp-sidebar sp-portal-nav w-64 flex flex-col fixed h-full z-40">
        <div className="p-4 border-b" style={{ borderBottomColor: 'var(--sp-border)' }}>
          <div className="flex items-center gap-3">
            <div className="sp-panel sp-panel--muted w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold">
              SP
            </div>
            <div>
              <h1 className="sp-value" style={{ fontWeight: 600 }}>Student Portal</h1>
              <p className="text-xs sp-muted">{student?.name || 'Student'}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {visibleItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === '/student-portal'}
              className={({ isActive }) => `sp-nav-link ${isActive ? 'sp-nav-link--active' : ''}`}
            >
              {item.icon ? <item.icon /> : <span className="sp-nav-dot" />}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderTopColor: 'var(--sp-border)' }}>
          <button
            onClick={() => {
              signOut();
              navigate('/student-portal/login');
            }}
            className="sp-button sp-button--secondary w-full"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main id="student-portal-main" tabIndex={-1} className="flex-1 ml-64 sp-portal">
        <header className="sp-header px-6 py-4 sticky top-0 z-30 space-y-3 sp-glass">
          <StudentPortalBanner settings={portalSettings} />
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="sp-value" style={{ fontSize: '18px', fontWeight: 600 }}>Welcome back</h2>
              <p className="text-sm sp-muted">{student?.reference || 'Student Portal'}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="sp-segment" role="radiogroup" aria-label="Theme mode">
                {['auto', 'light', 'dark'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={displaySettings.theme === mode}
                    className={displaySettings.theme === mode ? 'active' : ''}
                    onClick={() => setDisplaySettings((prev) => ({ ...prev, theme: mode }))}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
              <Avatar name={student?.name || 'Student'} size="sm" />
            </div>
          </div>
        </header>
        <div className="p-6 sp-portal-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
